import { motion } from "framer-motion";
import { Badge } from "../ui/badge";
import { ContentLabels } from "../../types/navigation";

interface LegalInfoProps {
  dldPermitNumber: string;
  qrCodeUrl: string;
  language?: string;
  contentLabels?: ContentLabels;
}

const LegalInfo = ({
  dldPermitNumber,
  qrCodeUrl = "https://ggfx-handh3.s3.eu-west-2.amazonaws.com/i/qr_code/72955_1943606353_118403.png",
  language,
  contentLabels
}: LegalInfoProps) => {
  // Default fallback labels if contentLabels is not provided
  const labels = contentLabels || {
    dldPermitNumber: 'DLD Permit Number',
    scanQrCode: 'Scan QR Code for more information',
    // Other required fields with fallbacks
    availableUnitTypes: 'Available Unit Types',
    unitSize: 'Size:',
    startingFrom: 'Starting from',
    downloadBrochure: 'Download Brochure',
    contactExpert: 'Contact Expert',
    viewDetails: 'View Details',
    requestInfo: 'Request Information',
    bookTour: 'Book a Tour',
    whatsappAction: 'WhatsApp',
    callAction: 'Call Now',
    learnMore: 'Learn More',
    viewAll: 'View All',
    showMore: 'Show More',
    showLess: 'Show Less',
    paymentPlan: 'Payment Plan',
    downPayment: 'Down Payment',
    dueBy: 'Due by',
    noPaymentPlans: 'No payment plans available at this time.',
    frequentlyAskedQuestions: 'Frequently Asked Questions',
    faqDescription: 'Find answers to common questions about our property and investment opportunities',
    primeLocation: 'Prime Location',
    nearbyPlaces: 'Nearby Places',
    keyDistances: 'Key Distances',
    noNearbyPlaces: 'No nearby places available.',
    noKeyDistances: 'No key distances available.',
    projectGallery: 'Project Gallery',
    closeGallery: 'Close gallery',
    fullName: 'Full Name',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email Address',
    phoneNumber: 'Phone Number',
    continue: 'Continue',
    back: 'Back',
    submit: 'Submit',
    submitting: 'Submitting...',
    thankYou: 'Thank You!',
    nameRequired: 'Name is required',
    emailRequired: 'Email is required',
    emailInvalid: 'Please enter a valid email',
    phoneRequired: 'Phone number is required'
  };

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          {/* Header */}
          <div className="text-center mb-12">
            <Badge 
              variant="secondary"
              className="mb-4 text-lg px-4 py-1"
            >
              Legal Information
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Verified by Dubai Land Department
            </h2>
          </div>

          {/* Content */}
          <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Left Column - QR Code */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex flex-col items-center space-y-4"
              >
                <div className="bg-white p-4 rounded-xl shadow-sm">
                  <img 
                    src={qrCodeUrl} 
                    alt="DLD Verification QR Code" 
                    className="w-48 h-48 object-contain"
                  />
                </div>
                <p className="text-sm text-gray-500 text-center">
                  {labels.scanQrCode}
                </p>
              </motion.div>

              {/* Right Column - Permit Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="space-y-6"
              >
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {labels.dldPermitNumber}
                  </h3>
                  <p className="text-2xl font-mono text-primary">
                    {dldPermitNumber}
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3 text-gray-600">
                    <svg 
                      className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" 
                      />
                    </svg>
                    <p className="text-sm">
                      This property listing has been reviewed and verified by Dubai Land Department
                    </p>
                  </div>
                  <div className="flex items-start space-x-3 text-gray-600">
                    <svg 
                      className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                      />
                    </svg>
                    <p className="text-sm">
                      All information displayed is accurate and compliant with DLD regulations
                    </p>
                  </div>
                </div>

                
              </motion.div>
            </div>
          </div>

          {/* Disclaimer */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xs text-gray-500 text-center mt-8"
          >
            The information provided on this website is subject to DLD regulations and guidelines. 
            All property details and availability are subject to change without notice.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

export default LegalInfo; 